import { useSupabaseCore, checkPermission, checkFeature } from '@packages/supabase'
import { JwtPayload } from '@supabase/supabase-js'
import { jwtDecode } from 'jwt-decode'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

type JWTClaims = JwtPayload & {
  tenant_id?: string
  role_id?: string
  is_owner?: boolean
  permissions?: string[]
  features?: string[]
}

type AdminSessionContextType = {
  user: User | undefined
  tenantId: string | null
  roleId: string | null
  permissions: string[]
  features: string[]
  isOwner: boolean
  p: (permission: string | boolean | undefined) => boolean
  f: (feature: string | boolean | undefined) => boolean
  loading: boolean
}

const AdminSessionContext = createContext<AdminSessionContextType | undefined>(
  undefined
)

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const { supabase, session, loading: authLoading } = useSupabaseCore()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | undefined>()
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [roleId, setRoleId] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [features, setFeatures] = useState<string[]>([])
  const [isOwner, setIsOwner] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (authLoading) {
        setLoading(true)
        return
      }

      if (!session) {
        setUser(undefined)
        setTenantId(null)
        setRoleId(null)
        setPermissions([])
        setFeatures([])
        setIsOwner(false)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const claims = jwtDecode(session.access_token) as JWTClaims
        if (cancelled) return
        setTenantId(claims.tenant_id || null)
        setRoleId(claims.role_id || null)
        setPermissions(claims.permissions || [])
        setFeatures(claims.features || [])
        setIsOwner(!!claims.is_owner)

        // Profile lookup: `auth.users.id` is the JWT subject. The bridge to
        // the profile row is `public.users.auth_id` — `public.users.id` is
        // an arbitrary profile PK (see documentation/wip/auth-id-profile-todo.md).
        // The two values coincide for accounts created via `handle_signup`,
        // but for invited / provisional profiles and any seed that separates
        // them, they MUST be different and we must look up by `auth_id`.
        const { data } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', session.user.id)
          .maybeSingle()
        if (!cancelled) {
          setUser((data || undefined) as User | undefined)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [authLoading, session, supabase])

  const p = useCallback(
    (permission: string | boolean | undefined) =>
      checkPermission(permission, { permissions, features, isOwner }),
    [isOwner, permissions, features]
  )

  const f = useCallback(
    (feature: string | boolean | undefined) => checkFeature(feature, features),
    [features]
  )

  const value = useMemo<AdminSessionContextType>(
    () => ({
      user,
      tenantId,
      roleId,
      permissions,
      features,
      isOwner,
      p,
      f,
      loading
    }),
    [user, tenantId, roleId, permissions, features, isOwner, p, f, loading]
  )

  return (
    <AdminSessionContext.Provider value={value}>
      {children}
    </AdminSessionContext.Provider>
  )
}

export const useSession = () => {
  const context = useContext(AdminSessionContext)
  if (context === undefined) {
    throw new Error('useSession must be used within Admin SessionProvider')
  }
  return context
}
